import { z } from 'zod'

export const ProviderApplicationSchema = z.object({
  id: z.number(),
  provider_id: z.number(),
  global_business_category_id: z.number().nullable(),
  business_category_name: z.string(),
})

const MarketplaceStoreAffiliationSchema = z.object({
  id: z.number(),
  name: z.string(),
  logo: z.string().nullable(),
})

export const MarketplaceStoreSchema = z.object({
  id: z.number(),
  status: z.number(),
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string(),
  username: z.string(),
  name: z.string(),
  legal_name: z.string().nullable(),
  logo: z.string().nullable(),
  address: z.string().nullable(),
  city: z.string().nullable(),
  global_state_id: z.number().nullable(),
  organization_description: z.string().nullable(),
  zipcode: z.string().nullable(),
  mailing_name: z.string().nullable(),
  mailing_email: z.string().nullable(),
  mailing_address_different: z.number().nullable(),
  mailing_address: z.string().nullable(),
  mailing_city: z.string().nullable(),
  customer_service_phone: z.string().nullable(),
  customer_service_email: z.string().nullable(),
  global_state_name: z.string().nullable(),
  header_background_color: z.string().nullable(),
  payments_support_email: z.string().nullable(),
  payments_support_phone: z.string().nullable(),
  accepting_new_clients: z.number(),
  phone_store: z.string().nullable(),
  mailing_global_state_id: z.number().nullable(),
  mailing_zip_code: z.string().nullable(),
  website: z.string().nullable(),
  remember_token: z.string().nullable(),
  user_has_accepted_text_notifications: z.number(),
  number_of_items_in_store: z.number().nullable(),
  access_2fa: z.number(),
  email_verified_at: z.string().nullable(),
  created_by: z.any().nullable(),
  created_on: z.any().nullable(),
  created_datetime: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  last_by: z.any().nullable(),
  last_on: z.any().nullable(),
  last_datetime: z.string().nullable(),
  deleted_at: z.any().nullable(),
  marketplace: z.number(),
  tax: z.number(),
  tax_percentage: z
    .union([z.string(), z.number()])
    .nullable()
    .transform((val) => (val === null ? null : Number(val))),
  surcharges: z.number(),
  surcharge_percentage: z
    .union([z.string(), z.number()])
    .nullable()
    .transform((val) => (val === null ? null : Number(val))),
  affiliations: z.array(MarketplaceStoreAffiliationSchema).default([]),
  provider_application: ProviderApplicationSchema.nullable(),
})

export const MarketplaceStorePaginatedSchema = z.object({
  current_page: z.number(),
  data: z.array(MarketplaceStoreSchema),
  first_page_url: z.string(),
  from: z.number().nullable(),
  last_page: z.number(),
  last_page_url: z.string(),
  links: z.array(
    z.object({
      url: z.string().nullable(),
      label: z.string(),
      active: z.boolean(),
    })
  ),
  next_page_url: z.string().nullable(),
  path: z.string(),
  per_page: z.union([z.string(), z.number()]).transform((val) => Number(val)),
  prev_page_url: z.string().nullable(),
  to: z.number().nullable(),
  total: z.number(),
  // Absent until the API supports scoping stores to the applicant's schools.
  has_students: z.boolean().optional(),
  has_school_links: z.boolean().optional(),
})

export type MarketplaceStorePaginated = z.infer<
  typeof MarketplaceStorePaginatedSchema
>
export type MarketplaceStore = z.infer<typeof MarketplaceStoreSchema>

export const MarketplaceAffiliationSchema = z.object({
  id: z.number(),
  name: z.string(),
  logo: z.string().nullable(),
  number_of_items_in_affiliation: z
    .union([z.string(), z.number()])
    .transform((val) => Number(val)),
})

export const MarketplaceAffiliationPaginatedSchema = z.object({
  current_page: z.number(),
  data: z.array(MarketplaceAffiliationSchema),
  first_page_url: z.string(),
  from: z.number().nullable(),
  last_page: z.number(),
  last_page_url: z.string(),
  links: z.array(
    z.object({
      url: z.string().nullable(),
      label: z.string(),
      active: z.boolean(),
    })
  ),
  next_page_url: z.string().nullable(),
  path: z.string(),
  per_page: z.union([z.string(), z.number()]).transform((val) => Number(val)),
  prev_page_url: z.string().nullable(),
  to: z.number().nullable(),
  total: z.number(),
})

export type MarketplaceAffiliation = z.infer<
  typeof MarketplaceAffiliationSchema
>
export type MarketplaceAffiliationPaginated = z.infer<
  typeof MarketplaceAffiliationPaginatedSchema
>
